/**
 * Hooks de lectura del dominio Actividad de usuarios (TanStack Query).
 *
 * Datos de servidor -> TanStack Query (`docs/frontend_conventions.md`, Datos): no
 * `useState`+`useEffect`+`fetch`. La red la gobierna `lib/api` (cookie httpOnly +
 * `X-Sede-Id`). El resumen se cachea por sede activa y período; el detalle por
 * usuario y ventana en días, y se deshabilita mientras no haya un usuario elegido.
 */
import { useQuery } from "@tanstack/react-query";

import { getActiveSedeId } from "@/lib/sede";

import { fetchActivityDetail, fetchActivitySummary } from "../api";
import { PERIOD_TO_DETAIL_DAYS } from "../constants";
import type { ActivityPeriod } from "../schemas";
import type { ActivityDetail, ActivitySummary } from "../types";
import {
  activityDetailQueryKey,
  activitySummaryQueryKey,
} from "./queryKeys";

/**
 * Resumen agregado de actividad para el período dado y la sede activa
 * (`GET /user_activity?period=`). La key incluye la sede para recargar al cambiarla.
 *
 * `enabled` (por defecto `true`) permite a la pantalla NO pedir el resumen cuando el
 * usuario carece del permiso `advanced_stats` (mismo patrón que `useEffectiveness`
 * en el dashboard): así no se solicita un dato que el actor no debe ver.
 */
export function useActivitySummary(period: ActivityPeriod, enabled = true) {
  const sedeId = getActiveSedeId();
  return useQuery<ActivitySummary>({
    queryKey: activitySummaryQueryKey(sedeId, period),
    queryFn: () => fetchActivitySummary(period),
    enabled,
    // Margen corto: evita refetch agresivo sin volver los datos rancios.
    staleTime: 30_000,
  });
}

/**
 * Detalle / timeline de un usuario; deshabilitado mientras `username` sea `null`.
 *
 * La ventana en días se deriva del período activo (mismo mapeo que el SSR), de modo
 * que el detalle cubre el rango que el usuario está viendo en el resumen.
 */
export function useActivityDetail(
  username: string | null,
  period: ActivityPeriod,
) {
  const days = PERIOD_TO_DETAIL_DAYS[period];
  return useQuery<ActivityDetail>({
    queryKey: activityDetailQueryKey(username ?? "", days),
    queryFn: () => fetchActivityDetail(username as string, days),
    enabled: username !== null,
    staleTime: 30_000,
  });
}
